import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! Welcome to Petsmart Puppies. How can I help you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickButtons = [
    { label: 'Available Puppies', message: 'What puppies do you have available?' },
    { label: 'Dog Food', message: 'Tell me about dog food products' },
    { label: 'Store Location', message: 'Where is your store located?' },
    { label: 'WhatsApp Us', message: 'whatsapp' }
  ];

  const handleQuickButton = (button) => {
    if (button.message === 'whatsapp') {
      const whatsappUrl = `https://wa.me/917217648679?text=${encodeURIComponent('Hello, I need help with puppies.')}`;
      window.open(whatsappUrl, '_blank');
      return;
    }
    handleSend(button.message);
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: messageText
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I am having trouble connecting. Please try calling us at 07217648679 or WhatsApp us!'
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed z-[995] chatbot-window"
          style={{
            bottom: '5rem',
            right: '1rem',
            width: '340px',
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'calc(100vh - 8rem)'
          }}
        >
          <div
            data-testid="chatbot-window"
            className="rounded-2xl overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              height: '450px',
              maxHeight: '70vh'
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-3"
              style={{ backgroundColor: '#0EA5E9' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <img
                    src="/petsmart-logo.png"
                    alt="Petsmart"
                    className="w-7 h-7 rounded-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Petsmart Assistant</h3>
                  <p className="text-white/80 text-xs">Online now</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                style={{ color: 'white' }}
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto p-3"
              style={{ backgroundColor: '#F3F4F6' }}
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  className="mb-3"
                  style={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div
                    className="text-sm"
                    style={{
                      maxWidth: '80%',
                      padding: '0.625rem 0.875rem',
                      borderRadius: message.role === 'user' 
                        ? '1rem 1rem 0.25rem 1rem' 
                        : '1rem 1rem 1rem 0.25rem',
                      backgroundColor: message.role === 'user' ? '#0EA5E9' : 'white',
                      color: message.role === 'user' ? 'white' : '#374151',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    {message.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start mb-3">
                  <div
                    className="px-4 py-2 rounded-xl bg-white text-sm"
                    style={{ color: '#6B7280' }}
                  >
                    <span className="animate-pulse">Typing...</span>
                  </div>
                </div>
              )}

              {/* Quick Buttons */}
              {messages.length === 1 && (
                <div className="mt-2">
                  <p className="text-xs mb-2" style={{ color: '#9CA3AF' }}>
                    Quick actions:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {quickButtons.map((button, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickButton(button)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                        style={{
                          border: '1px solid #0EA5E9',
                          backgroundColor: 'white',
                          color: '#0EA5E9'
                        }}
                      >
                        {button.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  data-testid="chatbot-input"
                  className="flex-1 px-3 py-2 rounded-full text-sm outline-none"
                  style={{
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#F9FAFB'
                  }}
                />
                <button
                  onClick={() => handleSend()}
                  disabled={isLoading || !input.trim()}
                  data-testid="chatbot-send-button"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    backgroundColor: isLoading || !input.trim() ? '#E5E7EB' : '#0EA5E9',
                    color: isLoading || !input.trim() ? '#9CA3AF' : 'white',
                    cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Button - Desktop only, positioned above WhatsApp button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chatbot-toggle-button"
          className="chatbot-toggle hidden md:flex items-center justify-center transition-all hover:scale-110 active:scale-100"
          style={{
            position: 'fixed',
            bottom: '5.5rem',
            right: '1.5rem',
            backgroundColor: '#0EA5E9',
            color: 'white',
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            boxShadow: '0 4px 20px rgba(14, 165, 233, 0.4)',
            zIndex: 985,
            border: 'none',
            cursor: 'pointer'
          }}
          aria-label="Open chat"
        >
          <Bot size={24} />
        </button>
      )}

      <style>{`
        /* Hide chatbot toggle on mobile - users can use WhatsApp instead */
        @media (max-width: 768px) {
          .chatbot-toggle {
            display: none !important;
          }
          .chatbot-window {
            bottom: 4rem !important;
            right: 0.5rem !important;
            left: 0.5rem !important;
            width: auto !important;
            max-width: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default ChatBot;
